import React, { memo, useCallback, useEffect, useState } from 'react'
import { Container, Menu, Dropdown } from 'semantic-ui-react'
import { NavLink, useHistory } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import _ from 'lodash'
import { logout, setSignInState } from '../../redux/Dashboard/UserReducer'
import { SpheroIcon, IconMyContent } from './Icons'
import { Trans } from 'react-i18next'
import { FAST, GOOGLECHANNEL, APPLECHANNEL, NAV_BAR_HEIGHT, SIGN_IN_STATE, SPHERO, STUDENT, TEACHER, GOOGLE_CLASSROOM_CHANNEL, CLEVERCHANNEL } from '../../constants'
import { IconStudentsWhite, IconUser } from '../Icons'
import styles from '../styles'
import { BLACK, BLUE, SHADOW, WHITE } from '../../colors'
import { clearClassroom } from '../../redux/Classroom/ClassroomReducer'

const menuStyle = {
  backgroundColor: BLUE,
  height: NAV_BAR_HEIGHT,
  boxShadow: `0px 2px 4px ${SHADOW}`
}

const iconStyle = {
  height: 32,
  width: 32
}

const textStyle = {
  fontSize: '0.938rem',
  fontWeight: 'bold'
}

export const NavBar = () => {
  const [color, setColor] = useState(WHITE)
  const { user, isLoggedIn, ageGate, signInState, admin, classType } = useSelector(state => state.authentication)
  const { navBarActiveItem: item, navbar } = useSelector(state => state.page)
  const { classroom } = useSelector(state => state.classroom)

  const dispatch = useDispatch()
  const history = useHistory()
  const incompleteUser = user.confirmedAt === null || signInState === SIGN_IN_STATE.MISSING_INFO || _.isEmpty(user.persona)

  const isMissingStandardInfo = () => {
    if (user && user?.preferences) {
      return !user.preferences.toc || !user.preferences.privacyPolicy || _.isEmpty(user.username) || _.isEmpty(user.firstName)
    }
    return true
  }

  const isMissingSignature = () => {
    return _.isEmpty(user.preferences.signature)
  }

  const changeStateAndGo = (nextState) => {
    dispatch(setSignInState(nextState))
    history.push(`/signup/${nextState}`)
  }

  const updateInfo = () => {
    if (user.registrationChannel === GOOGLECHANNEL || user.registrationChannel === APPLECHANNEL) {
      changeStateAndGo(SIGN_IN_STATE.SSO_CHOOSE_ROLE)
    } else if (_.isEmpty(user.persona)) {
      changeStateAndGo(SIGN_IN_STATE.MISSING_ROLE)
    } else {
      changeStateAndGo(SIGN_IN_STATE.MISSING_INFO)
    }
  }

  useEffect(() => {
    // Seems to be a bug in semantic ui. Does not correctly remove the active class in all cases
    if (item !== 'splash') {
      const div = document.getElementById('sphero-central')
      if (div) {
        div.classList.remove('active')
      }
    }
  }, [item, history.location])

  useEffect(() => {
    if (!isLoggedIn) return
    if (user?.guardianEmail !== null) return
    if (user?.persona === STUDENT && (user?.rosters.length > 0 || user?.memberships?.length > 0)) return
    // Done as else if statements so that we check things in order, we don't want to popup several modals to the user at once
    if (user && _.isEmpty(user?.persona)) {
      changeStateAndGo(SIGN_IN_STATE.SSO_CHOOSE_ROLE)
    } else {
      // prevents checks while user state is updating
      if (user) {
        if (user?.persona === STUDENT) {
          if (user.registrationChannel !== GOOGLE_CLASSROOM_CHANNEL && user.registrationChannel !== CLEVERCHANNEL) {
            if (user?.ageOfConsent === null) {
              changeStateAndGo(SIGN_IN_STATE.AGE_GATE)
            } else {
              if (ageGate) {
                if (user?.ageOfConsent && isMissingStandardInfo()) {
                  changeStateAndGo(SIGN_IN_STATE.MISSING_INFO)
                }
              }
            }
          }
        } else if (isMissingStandardInfo() || isMissingSignature()) {
          changeStateAndGo(SIGN_IN_STATE.MISSING_INFO)
        } else if (user?.persona === TEACHER && _.isEmpty(user?.vpcSchool)) {
          changeStateAndGo(SIGN_IN_STATE.MISSING_INFO)
        } else if (_.isEmpty(user?.confirmedAt)) {
          changeStateAndGo(SIGN_IN_STATE.VERIFY_EMAIL)
        }
      }
    }
  }, [isLoggedIn, user?.persona, user?.ageOfConsent, ageGate, user?.confirmedAt])

  useEffect(() => {
    if (item === 'user') setColor(WHITE)
    else setColor(BLACK)
  }, [item])

  const logOut = (e) => {
    e.preventDefault()
    dispatch(logout())
    dispatch(clearClassroom())
    history.push('/sphero/home')
  }

  const avatar = useCallback((isActive) => {
    let name = ''
    if (user) {
      name = user.username ? user.username : user.email
    }
    if (classroom?.classType === FAST && user.persona === STUDENT) {
      name = user.firstName + ' ' + user.lastName
    }

    return (
      <div style={styles.centerColumnStyle}>
        <IconUser color={isActive ? WHITE : BLACK} style={iconStyle} />
        <p style={{ ...textStyle, color: isActive ? WHITE : BLACK }}>
          {name}
        </p>
      </div>
    )
  }, [isLoggedIn, user])

  return (
    navbar &&
      <Menu style={menuStyle} fixed='top' inverted icon='labeled'>
        <Container fluid>
          <Menu.Item
            as={classType !== FAST && NavLink}
            to='/sphero/home'
            header
            name={SPHERO}
            active={item === SPHERO}
            className='top-nav-bar'
          >
            <SpheroIcon size='110' color={item === SPHERO ? WHITE : BLACK} />
          </Menu.Item>
          <Menu.Menu position='right'>
            {isLoggedIn && !incompleteUser &&
              <Menu.Menu position='right'>
                <Menu.Item
                  as={NavLink}
                  to={classType === FAST ? '/my_content/activities' : '/my_content/programs'}
                  name='my_content'
                  active={item === 'my_content'}
                  className='top-nav-bar'
                >
                  <div style={styles.centerColumnStyle}>
                    <IconMyContent color={item === 'my_content' ? WHITE : BLACK} style={iconStyle} />
                    <p style={{ ...textStyle, color: item === 'my_content' ? WHITE : BLACK }}>
                      <Trans i18nKey='sidebar.my_content'>My Content</Trans>
                    </p>
                  </div>
                </Menu.Item>
                {user && user.persona !== STUDENT && !incompleteUser &&
                  <Menu.Item
                    href={user.persona === TEACHER ? '/classrooms' : '/family'}
                    name='classroom'
                    active={item === 'classroom'}
                    className='top-nav-bar'
                  >
                    <div style={styles.centerColumnStyle}>
                      <IconStudentsWhite color={item === 'classroom' ? WHITE : BLACK} style={iconStyle} />
                      <p style={{ ...textStyle, color: item === 'classroom' ? WHITE : BLACK }}>
                        <Trans i18nKey={user.persona === TEACHER ? 'navbar.classes' : 'navbar.family'} />
                      </p>
                    </div>
                  </Menu.Item>}
              </Menu.Menu>}
            {isLoggedIn &&
              <Menu.Item
                name='user'
                active={item === 'user'}
                className='top-nav-bar'
              >
                <Dropdown className='top-nav-bar' style={{ color: 'white', marginTop: '28px' }} trigger={avatar(item === 'user')}>
                  <Dropdown.Menu>
                    {incompleteUser && <Dropdown.Item onClick={() => updateInfo()}><Trans i18nKey='navbar.complete_login'>Complete Login</Trans></Dropdown.Item>}
                    {(classroom?.classType !== FAST || user.persona !== STUDENT) && <Dropdown.Item as={NavLink} to='/profile/settings'><Trans i18nKey='navbar.profile' /></Dropdown.Item>}
                    {(classroom?.classType !== FAST || user.persona !== STUDENT) && <Dropdown.Item as={NavLink} to='/profile/notifications'><Trans i18nKey='navbar.messages' /></Dropdown.Item>}
                    {admin && <Dropdown.Item as={NavLink} to='/moderation' text={<Trans i18nKey='navbar.moderate'>Moderate</Trans>} />}
                    <Dropdown.Item text={<Trans i18nKey='navbar.sign_out' />} onClick={logOut} />
                  </Dropdown.Menu>
                </Dropdown>
              </Menu.Item>}
            {!isLoggedIn &&
              <Menu.Item
                href='/signin'
                as='a'
                header
                name='user'
                active={item === 'user'}
                className='top-nav-bar'
              >
                <IconUser color={color} />
                <span style={{ color, fontSize: '0.938rem', fontWeight: 'bold' }}>
                  <Trans i18nKey='button.sign_in' />
                </span>
              </Menu.Item>}
          </Menu.Menu>
        </Container>
      </Menu>
  )
}

export default memo(NavBar)
